import React, { useState, useCallback, useEffect } from "react";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  FileText,
  Loader2,
  ReceiptText,
} from "lucide-react";
import Headerdash from "./head";
import Sidebar from "./sidebar";
import BillingPricing from "../StatusPages/ChangePlanPricing";
import PlanChangeResult from "./DashboardComponent/PlanChangeResult";

const BillingShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const formatBillingDate = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusStyles = {
  Succeeded: "bg-green-50 text-green-700 ring-green-100",
  Failed: "bg-red-50 text-red-700 ring-red-100",
  Active: "bg-green-50 text-green-700 ring-green-100",
  Cancelled: "bg-red-50 text-red-700 ring-red-100",
  Canceled: "bg-red-50 text-red-700 ring-red-100",
  Expired: "bg-amber-50 text-amber-700 ring-amber-100",
};

const formatTransactionAmount = (entry) => {
  if (typeof entry.amount !== "number") return "N/A";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: (entry.currency || "usd").toUpperCase(),
    }).format(entry.amount / 100);
  } catch {
    return `${(entry.amount / 100).toFixed(2)} ${entry.currency || "USD"}`;
  }
};

const getStatusClass = (status) =>
  statusStyles[status] || "bg-slate-100 text-slate-700 ring-slate-200";

const BillingSummaryCard = ({ icon: Icon, label, value, helper }) => (
  <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-semibold uppercase text-gray-500">
          {label}
        </p>
        <p className="mt-3 text-2xl font-extrabold text-gray-900">{value}</p>
      </div>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
    </div>
    {helper && <p className="mt-3 text-sm text-gray-500">{helper}</p>}
  </div>
);

const PurchaseHistory = ({ subscriptions }) => {
  if (!subscriptions) {
    return (
      <div className="flex min-h-[180px] items-center justify-center rounded-md border border-slate-200 bg-white p-6 text-sm font-semibold text-gray-500 shadow-sm">
        <Loader2 className="mr-2 h-4 w-4 animate-spin text-purple-700" />
        Loading purchase history
      </div>
    );
  }

  if (!subscriptions.length) {
    return (
      <div className="rounded-md border border-slate-200 bg-white p-6 text-center shadow-sm">
        <span className="mx-auto grid h-11 w-11 place-items-center rounded-md bg-slate-100 text-gray-500">
          <ReceiptText className="h-5 w-5" aria-hidden="true" />
        </span>
        <h3 className="mt-4 text-lg font-extrabold text-gray-900">
          No purchase history yet
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Your completed plan changes and renewals will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="whitespace-nowrap px-5 py-3 font-semibold uppercase text-gray-500">
                Billing date
              </th>
              <th className="whitespace-nowrap px-5 py-3 font-semibold uppercase text-gray-500">
                Period ends
              </th>
              <th className="whitespace-nowrap px-5 py-3 font-semibold uppercase text-gray-500">
                Plan
              </th>
              <th className="whitespace-nowrap px-5 py-3 font-semibold uppercase text-gray-500">
                Amount
              </th>
              <th className="whitespace-nowrap px-5 py-3 font-semibold uppercase text-gray-500">
                Status
              </th>
              <th className="whitespace-nowrap px-5 py-3 font-semibold uppercase text-gray-500">
                Access
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {subscriptions.map((entry, index) => (
              <tr key={entry.historyKey || entry._id || `${entry.subscriptionStartDate}-${index}`} className="transition hover:bg-purple-50/40">
                <td className="whitespace-nowrap px-5 py-4 font-semibold text-gray-900">
                  {formatBillingDate(entry.transactionDate || entry.subscriptionStartDate)}
                </td>
                <td className="whitespace-nowrap px-5 py-4 text-gray-600">
                  {formatBillingDate(entry.subscriptionEndDate)}
                </td>
                <td className="whitespace-nowrap px-5 py-4 text-gray-700">
                  {entry.beforePlan || "N/A"}
                </td>
                <td className="whitespace-nowrap px-5 py-4 font-semibold text-gray-700">
                  {formatTransactionAmount(entry)}
                </td>
                <td className="whitespace-nowrap px-5 py-4">
                  <span
                    className={`inline-flex rounded-md px-2.5 py-1 text-xs font-bold uppercase ring-1 ${getStatusClass(
                      entry.subscriptionStatus
                    )}`}
                  >
                    {entry.subscriptionStatus || "Unknown"}
                  </span>
                </td>
                <td className="whitespace-nowrap px-5 py-4">
                  <span className={`inline-flex rounded-md px-2.5 py-1 text-xs font-bold uppercase ring-1 ${
                    entry.subscriptionAccess
                      ? "bg-green-50 text-green-700 ring-green-100"
                      : "bg-slate-100 text-slate-600 ring-slate-200"
                  }`}>
                    {entry.subscriptionAccess ? "Active" : "Inactive"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Billing() {
  const [billingRevision, setBillingRevision] = useState(0);
  const refreshAfterPlanChange = useCallback(() => setBillingRevision((value) => value + 1), []);
  const [allSubscriptionData, setAllSubscriptionData] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState("");

  const handleAllSubscriptionData = useCallback((data) => {
    setAllSubscriptionData(data || []);
  }, []);

  const handleCurrentSubscriptionData = useCallback((data) => {
    setCurrentSubscription(data);
  }, []);

  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        const response = await fetch("/api/category/main");
        if (!response.ok) {
          throw new Error("Failed to fetch subscription plans");
        }

        const data = await response.json();
        const mappedPlans = data.map((plan) => {
          const cancelledMRP = parseFloat(
            plan.dynamicPricing?.basePrice || "0"
          );
          const price = parseFloat(plan.price || "0");
          const savingsPercent = cancelledMRP
            ? Math.round(((cancelledMRP - price) / cancelledMRP) * 100)
            : 0;
          const freeMonths = plan.dynamicPricing?.freeMonths || "No freeMonths";

          return {
            plan: plan.name,
            price,
            planID: plan.planID,
            duration: plan.months,
            features: plan.description.split("\n"),
            savings: savingsPercent > 0 ? `${savingsPercent}` : "No Savings",
            additionalDetails:
              plan.additionalDetails || "No additional details",
            promotion: plan.promotion || "No promotion",
            freeMonths,
            cancelledMRP:
              cancelledMRP > 0 ? `$${cancelledMRP.toFixed(2)}` : "$0",
          };
        });

        setPlans(mappedPlans);
      } catch (err) {
        setError("Failed to fetch subscription plans");
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPricingData();
  }, []);

  const activePlan = currentSubscription?.currentPlan || "N/A";
  const billingStatus = currentSubscription?.cancelAtPeriodEnd && currentSubscription?.subscriptionAccess ? "Renewal cancelled" : currentSubscription?.subscriptionStatus || "N/A";

  return (
    <BillingShell>
      <PlanChangeResult onComplete={refreshAfterPlanChange} />
      <section className="mb-6 flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            Plans and billing
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            Manage your PrepNexis plan
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">
            Review your current subscription, compare plans, and keep billing
            history in one place.
          </p>
        </div>
      </section>

      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-md border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <section className="mb-6 grid gap-4 md:grid-cols-3">
        <BillingSummaryCard
          icon={CreditCard}
          label="Current plan"
          value={activePlan}
          helper="Your active access level"
        />
        <BillingSummaryCard
          icon={CheckCircle2}
          label="Status"
          value={billingStatus}
          helper="Subscription state"
        />
        <BillingSummaryCard
          icon={CalendarClock}
          label={currentSubscription?.cancelAtPeriodEnd ? "Access ends" : "Next billing"}
          value={formatBillingDate(currentSubscription?.cancelAtPeriodEnd ? currentSubscription?.subscriptionEndDate : currentSubscription?.nextBillingDate)}
          helper={currentSubscription?.cancelAtPeriodEnd ? "Your plan stays available until this date" : "Your next scheduled renewal"}
        />
      </section>

      <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase text-purple-700">
              Available plans
            </p>
            <h2 className="text-xl font-extrabold text-gray-900">
              Pick the plan that fits your prep window
            </h2>
          </div>
          {loadingPlans && (
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin text-purple-700" />
              Loading plans
            </span>
          )}
        </div>

        <BillingPricing
          key={billingRevision}
          onAllSubscriptionData={handleAllSubscriptionData}
          onCurrentSubscriptionData={handleCurrentSubscriptionData}
          plans={plans}
        />
      </section>

      <section className="mt-6">
        <div className="mb-4 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-md bg-purple-50 text-purple-700">
            <FileText className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold uppercase text-purple-700">
              Purchase history
            </p>
            <h2 className="text-xl font-extrabold text-gray-900">
              Billing timeline
            </h2>
          </div>
        </div>

        <PurchaseHistory subscriptions={allSubscriptionData} />
      </section>
    </BillingShell>
  );
}
